import React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GlobalStyles } from 'src/theme';
import { StorybookLegacyGlobalStyles } from 'src/ui/storybook-legacy-styles';
import { AsyncModalPortal } from 'src/ui/asyncModalAction';
import { Toaster } from '@/components/ui/sonner';
import { GlobalStyles as GlobalStylesLocal } from './global.styles';
import RouterFactory, { useRouting } from '../routing/child-router-factory';
import { createConfig } from './route-config';

import { routingMap } from '../routing/routing-map';
import AntdI18n from './AntdI18n';
import { CompanyStoreProvider } from './Company.store';
import { ThemeStoreProvider } from './Theme.store';
import Router from './router';
import { UserStoreProvider } from './User.store';
import { PropertyStoreProvider } from './Property.store';
import { DevBackendIndicator } from 'src/components/dev-tools/DevBackendIndicator';
import { FeedbackWidget } from 'src/components/feedback/FeedbackWidget';
import { RouteProvider } from 'src/providers/route-provider';
import { ThemeProvider } from 'src/providers/theme-provider';
import Browserlessv1 from './browerless-v1';
import Browserlessv2 from './browerless-v2';

// The public skip-hire request door (PRODUCT-485) is its own chunk: it only
// mounts on /request/* and must stay out of the eager first-paint bundle.
const RequestDoor = React.lazy(() =>
  import('src/components/request/RequestDoor').then((m) => ({
    default: m.RequestDoor,
  }))
);

// One client for the app lifetime. Previously this was constructed inside the
// App render body, so every RouterContext update (login, role resolution) made a
// fresh client and wiped the entire query cache. Module scope keeps it stable.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry deterministic 4xx (bad request / auth / not found); retry
      // server (5xx) and network errors at most once so a transient failure gets
      // one more shot without trapping the user behind 3 slow attempts. A
      // CORS-blocked 500 has no readable response → status is undefined → falls
      // through to a single retry, which is the right behavior for network errors.
      retry: (failureCount, error) => {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      // Stop the refetch storm (and Sentry traces) every time the user tabs back.
      refetchOnWindowFocus: false,
    },
  },
});

export const App = () => {
  const { routingMap: map } = useRouting();

  return (
    <QueryClientProvider client={queryClient}>
      {import.meta.env.REACT_APP_SHOW_QUERY_DEVTOOLS === 'true' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
      <StorybookLegacyGlobalStyles />
      {/* Dark mode is tenant-driven (derived from company.secondary_color) and
          owned by Theme.store, which toggles the `.dark-mode` class directly.
          This ThemeProvider no longer touches that class — it's kept only for
          API stability, so `defaultTheme="light"` below is inert. */}
      <ThemeProvider defaultTheme="light">
        <BrowserRouter basename={window.WH_SS_INIT_ROUTE || ''}>
          <RouteProvider>
            <Routes>
              {/* Reversed version to support new calendar */}
              <Route
                path="/calendar/v2"
                element={
                  <CompanyStoreProvider>
                    <Browserlessv1 />
                  </CompanyStoreProvider>
                }
              />
              <Route
                path="/calendar"
                element={
                  <CompanyStoreProvider>
                    <Browserlessv2 />
                  </CompanyStoreProvider>
                }
              />
              {/*
                Public request door (PRODUCT-485): a visitor with NO property
                verifies their email and requests a skip. Mounted beside the
                app on purpose — UserStoreProvider / PropertyStoreProvider gate
                everything under "/*" on a property, which is exactly what this
                visitor does not have. It shares CompanyStoreProvider (tenant,
                flags, branding) and the theme so it reads as the same portal.
              */}
              <Route
                path="/request/*"
                element={
                  <>
                    <Toaster richColors theme="light" />
                    <CompanyStoreProvider>
                      <ThemeStoreProvider>
                        <AntdI18n>
                          <GlobalStyles />
                          <GlobalStylesLocal />
                          <React.Suspense fallback={null}>
                            <RequestDoor />
                          </React.Suspense>
                        </AntdI18n>
                      </ThemeStoreProvider>
                    </CompanyStoreProvider>
                  </>
                }
              />
              <Route
                path="/*"
                element={
                  <>
                    <AsyncModalPortal />
                    <DevBackendIndicator />
                    <Toaster richColors theme="light" />
                    <CompanyStoreProvider>
                      <>
                        <FeedbackWidget />
                        <ThemeStoreProvider>
                          <AntdI18n>
                            <GlobalStyles />
                            <GlobalStylesLocal />
                            <UserStoreProvider>
                              <PropertyStoreProvider>
                                <Router>
                                  <RouterFactory
                                    config={createConfig(map)}
                                    defaultRedirect={
                                      routingMap.app.selfService._
                                    }
                                  />
                                </Router>
                              </PropertyStoreProvider>
                            </UserStoreProvider>
                          </AntdI18n>
                        </ThemeStoreProvider>
                      </>
                    </CompanyStoreProvider>
                  </>
                }
              />
            </Routes>
          </RouteProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};
